// Conversation avec l'usager d'un dossier (fond distinct des notes internes). La route
// GET /dossiers/:id/conversation est livrée par la messagerie usager : tant qu'elle répond 404,
// l'emplacement affiche un état « bientôt disponible » honnête, sans faux contenu.
import { Bandeau, Bouton, EtatVide, Squelette, ZoneTexte } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { ErreurApi } from '../../../api/erreurs';
import { AffichageErreur } from '../../../commun/EtatRequete';
import { dateHeure } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import commun from '../../../commun/commun.module.css';
import styles from '../metier.module.css';

interface MessageConversation {
  id: string;
  contenu: string;
  cree_le?: string;
  envoye_le?: string;
  auteur?: { nom?: string | null; type?: string | null; appartenance_id?: string | null; compte_id?: string | null } | null;
  usager?: boolean;
}

interface Conversation {
  id?: string;
  messages?: MessageConversation[];
  elements?: MessageConversation[];
  non_lus?: number;
}

export function ConversationUsager({ dossierId, peutRepondre }: { dossierId: string; peutRepondre: boolean }) {
  const [texte, setTexte] = useState('');
  const cle = ['dossiers', 'conversation', dossierId];
  const conversation = useQuery({
    queryKey: cle,
    queryFn: ({ signal }) => requete<Conversation>(`/dossiers/${dossierId}/conversation`, { signal }),
    retry: false,
  });
  const envoi = useAction({
    executer: () => requete(`/dossiers/${dossierId}/conversation/messages`, { methode: 'POST', corps: { contenu: texte } }),
    succes: 'Message envoyé à l\'usager.',
    invalider: [cle],
    apres: () => setTexte(''),
  });
  if (conversation.isPending) return <Squelette lignes={3} />;
  if (conversation.error) {
    if (conversation.error instanceof ErreurApi && conversation.error.statut === 404) {
      return (
        <EtatVide titre="Conversation usager bientôt disponible" icone={MessageSquare}>
          <p>
            La messagerie entre le service et l'usager est en cours de livraison (lot L1-B). Les notes internes restent séparées et ne lui seront jamais visibles. En attendant, l'usager est informé par les notifications de chaque changement de statut.
          </p>
        </EtatVide>
      );
    }
    return <AffichageErreur erreur={conversation.error} fonction="La conversation usager" />;
  }
  const messages = conversation.data.messages ?? conversation.data.elements ?? [];
  return (
    <div className={styles.conversation}>
      <p className="cc-etiquette">Message visible par l'usager</p>
      {messages.length === 0 ? <p className={commun.na}>Aucun message échangé.</p> : null}
      <ol className={styles.messages}>
        {messages.map((m) => (
          <li key={m.id} className={styles.message} data-usager={m.usager || m.auteur?.type === 'usager' || undefined}>
            <span className={styles.auteurMessage}>
              {m.auteur?.nom ?? (m.usager || m.auteur?.type === 'usager' ? 'Usager' : 'Service')}, {dateHeure(m.cree_le ?? m.envoye_le ?? null)}
            </span>
            <span>{m.contenu}</span>
          </li>
        ))}
      </ol>
      {peutRepondre ? (
        <form
          className={styles.pile}
          onSubmit={(e) => {
            e.preventDefault();
            if (texte.trim()) envoi.lancer(undefined);
          }}
        >
          <ZoneTexte libelle="Répondre à l'usager (visible sur son portail et par ses canaux)" value={texte} rows={2} onChange={(e) => setTexte(e.target.value)} />
          {envoi.messageErreur ? <Bandeau type="erreur">{envoi.messageErreur}</Bandeau> : null}
          <div>
            <Bouton type="submit" variante="secondaire" chargement={envoi.enCours} disabled={!texte.trim()}>
              Envoyer
            </Bouton>
          </div>
        </form>
      ) : null}
    </div>
  );
}
